import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, FileText, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const statusColor = (status) => {
  switch (status) {
    case "Published": return "text-plasma-teal border-plasma-teal/40";
    case "Living Document": return "text-gold border-gold/50";
    case "In Progress": return "text-amber-300 border-amber-300/40";
    default: return "text-slate-400 border-white/20";
  }
};

const PaperCard = ({ paper, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 0.06}>
      <div
        className={`group glass-card rounded-xl p-7 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-plasma-teal/30 hover:shadow-[0_10px_40px_rgba(29,233,182,0.1)] ${
          paper.featured ? "ring-1 ring-gold/25" : ""
        }`}
        data-testid={`paper-card-${paper.slug}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${statusColor(paper.status)}`}>
              {paper.status}
            </span>
            <span className="font-mono text-xs text-slate-500">{paper.year}</span>
          </div>
          {paper.featured && <Sparkles className="h-4 w-4 text-gold" strokeWidth={1.5} />}
        </div>

        <h3 className="mt-5 font-display text-2xl md:text-3xl font-light leading-tight text-white">
          {paper.title}
        </h3>

        <p className={`mt-4 font-sans text-sm font-light leading-relaxed text-slate-400 ${open ? "" : "line-clamp-2"}`}>
          {paper.abstract}
        </p>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-5 flex flex-wrap gap-2">
                {paper.topicTags?.map((t) => (
                  <span key={t} className="rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] tracking-wide text-slate-400">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-5">
          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-plasma-teal"
            data-testid={`paper-expand-${paper.slug}`}
          >
            {open ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {open ? "Less" : "Details"}
          </button>

          {paper.pdfUrl ? (
            <a
              href={paper.pdfUrl}
              target="_blank"
              rel="noreferrer"
              download
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-plasma-teal transition-colors hover:text-white"
              data-testid={`paper-pdf-${paper.slug}`}
            >
              <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> View PDF
            </a>
          ) : (
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-slate-600">
              {paper.status === "Living Document" ? "Evolving" : "Coming soon"}
            </span>
          )}
        </div>
      </div>
    </Reveal>
  );
};

export const Research = ({ papers }) => (
  <section id="research" className="relative py-28 md:py-36" data-testid="research-section">
    <div className="section-shell">
      <SectionHeader
        index="01"
        overline="The Papers"
        title="Research & Ongoing Synthesis"
        description="Peer-facing papers, living manifestos, and studies in progress — a rigorous archive of the resonance framework as it develops."
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {papers.map((p, i) => (
          <PaperCard key={p.slug} paper={p} i={i} />
        ))}
      </div>
    </div>
  </section>
);
